import {
  REPOSITORY_INTELLIGENCE_SYMBOL_KINDS,
  type RepositoryIntelligenceSymbolKind,
} from '@ashniva/types';
import { Button, Card, EmptyState, FormField, Input, Select, SkeletonText } from '@ashniva/ui';
import { useState, type FormEvent } from 'react';

import { useRiSymbolsQuery, type SymbolFilter } from '../api-code';
import { linesLabel } from '../ri-text';
import { useWorkspace } from '../workspace-context';
import { ProblemNotice } from './ProblemNotice';
import { SymbolDetail } from './SymbolDetail';

const KINDS = [
  { value: '', label: 'Any kind' },
  ...REPOSITORY_INTELLIGENCE_SYMBOL_KINDS.map((kind) => ({ value: kind, label: kind })),
];

/** Symbols by name, kind or file, and one symbol with who calls it, what it calls and its tests (P14-05). */
export function SymbolsPanel() {
  const { linkId } = useWorkspace();
  const [draft, setDraft] = useState<SymbolFilter>({ q: '', kind: '', path: '' });
  const [filter, setFilter] = useState<SymbolFilter>({ q: '', kind: '', path: '' });
  const [selected, setSelected] = useState<string | null>(null);
  const symbols = useRiSymbolsQuery(linkId, filter, true);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setFilter({ q: draft.q.trim(), kind: draft.kind, path: draft.path.trim() });
    setSelected(null);
  };
  const items = symbols.data?.items ?? [];

  return (
    <div className="ri-files">
      <Card title="Symbols">
        <form className="ri-form" onSubmit={onSubmit} aria-label="Find symbols">
          <FormField label="Name contains">
            <Input
              value={draft.q}
              maxLength={200}
              onChange={(event) => setDraft({ ...draft, q: event.target.value })}
            />
          </FormField>
          <FormField label="Kind">
            <Select
              value={draft.kind}
              options={KINDS}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  kind: event.target.value as RepositoryIntelligenceSymbolKind | '',
                })
              }
            />
          </FormField>
          <FormField label="In file" hint="A path such as src/payroll/run.ts">
            <Input
              value={draft.path}
              maxLength={4096}
              onChange={(event) => setDraft({ ...draft, path: event.target.value })}
            />
          </FormField>
          <Button type="submit" size="sm">
            Find
          </Button>
        </form>
        {symbols.isLoading ? (
          <div aria-busy="true">
            <span className="sr-only">Loading symbols</span>
            <SkeletonText lines={6} />
          </div>
        ) : null}
        {symbols.isError ? (
          <ProblemNotice error={symbols.error} onRetry={() => void symbols.refetch()} size="sm" />
        ) : null}
        {symbols.isSuccess && items.length === 0 ? (
          <EmptyState
            size="sm"
            icon="○"
            title="No symbols found"
            description="Try part of the name, another kind, or no file."
          />
        ) : null}
        {items.length > 0 ? (
          <ul className="ri-list" aria-label="Symbols found">
            {items.map((symbol) => (
              <li key={symbol.id}>
                <button
                  type="button"
                  className="ri-file-link"
                  aria-current={symbol.id === selected ? 'true' : undefined}
                  onClick={() => setSelected(symbol.id)}
                >
                  {symbol.qualifiedName || symbol.name}
                </button>
                <div className="ri-muted">
                  {symbol.kind} · {symbol.path}, {linesLabel(symbol.startLine, symbol.endLine)}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>
      <Card title="Symbol">
        {selected ? (
          <SymbolDetail symbolId={selected} />
        ) : (
          <EmptyState size="sm" icon="○" title="Choose a symbol to see its code and who uses it" />
        )}
      </Card>
    </div>
  );
}
